<template>
  <div style="display: none">
    <div ref="marker" class="o-map-editable-circle-marker">
      <o-icon class="o-map-editable-circle-marker__icon" name="map-marker-alt" />
    </div>
  </div>
</template>

<script>
import styles from '@styles/_variables.scss'

export default {
  name: 'OMapEditableCircleMarker',

  inject: ['onMap'],

  props: {
    location: {
      type: Array,
      default: null,
    },
    radius: {
      type: Number,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    fillColor: {
      type: String,
      default: styles.gray,
    },
    strokeColor: {
      type: String,
      default: styles.gray,
    },
    strokeWeight: {
      type: Number,
      default: 8,
    },
    minRadius: {
      type: Number,
      default: 1,
    },
  },

  created() {
    const { DomEvent } = require('leaflet')

    this.DomEvent = DomEvent
  },

  mounted() {
    const { circle, LayerGroup, divIcon, marker } = require('leaflet')

    this.$centerMarker = marker(this.location, {
      icon: divIcon({
        iconSize: null,
        iconAnchor: [13, 26],
        className: 'o-map-editable-circle-marker',
        html: this.$refs['marker'],
      }),
      draggable: true,
    })

    this.$circleMarker = circle(this.location, {
      stroke: false,
      fillOpacity: 0.2,
      fillColor: styles[this.fillColor],
      radius: this.radius,
      className: 'o-map-editable-circle-marker__zone',
    })

    this.$circleMarkerBorder = circle(this.location, {
      fill: false,
      color: styles[this.strokeColor],
      radius: this.radius - this.strokeWeight / 2,
      weight: this.strokeWeight,
      opacity: 0.6,
      className: 'o-map-editable-circle-marker__zone',
    })

    this.createTooltip(this.$centerMarker, this.label)
    this.createTooltip(this.$circleMarker, this.label)

    this.onMap.then((map) => {
      this.$layerGroup = new LayerGroup([
        this.$centerMarker,
        this.$circleMarker,
        this.$circleMarkerBorder,
      ])
      map.addLayer(this.$layerGroup)

      this.enableEvents()
    })
  },

  watch: {
    label(newLabel) {
      this.$centerMarker.setTooltipContent(newLabel)
      this.$circleMarker.setTooltipContent(newLabel)
    },

    fillColor(fillColor) {
      this.$circleMarker.setStyle({ fillColor: styles[fillColor] })
    },

    strokeColor(strokeColor) {
      this.$circleMarkerBorder.setStyle({ color: styles[strokeColor] })
    },

    strokeWeight(strokeWeight) {
      this.$circleMarkerBorder.setStyle({ weight: strokeWeight })
    },

    location(location) {
      this.$centerMarker.setLatLng(location)
      this.$circleMarker.setLatLng(location)
      this.$circleMarkerBorder.setLatLng(location)
    },

    radius(newRadius) {
      this.$circleMarker.setRadius(newRadius)
      this.$circleMarkerBorder.setRadius(newRadius - this.strokeWeight / 2)
    },
  },

  methods: {
    createTooltip(marker, label) {
      if (!label) {
        return
      }

      marker.bindTooltip(label, {
        direction: 'top',
        offset: [0, -26],
        className: 'o-map-editable-circle-marker__tooltip',
      })
    },

    updateCursorStyle(e) {
      const map = e.target._map
      const centerPoint = map.latLngToContainerPoint(e.target.getLatLng())
      const mousePoint = e.containerPoint

      const x = mousePoint.x - centerPoint.x
      const y = mousePoint.y - centerPoint.y

      const angle = Math.atan2(y, x) * (180 / Math.PI)
      const index = Math.round((angle + 360) / 45) % 8

      const cursors = [
        'e-resize',
        'se-resize',
        's-resize',
        'sw-resize',
        'w-resize',
        'nw-resize',
        'n-resize',
        'ne-resize',
      ]

      map.getContainer().style.cursor = cursors[index]
    },

    onClickCenterMarker(e) {
      this.DomEvent.stopPropagation(e)
      this.$emit('click', e)
    },

    onClickCircleMarker(e) {
      this.DomEvent.stopPropagation(e)
    },

    onClickCircleMarkerBorder(e) {
      this.DomEvent.stopPropagation(e)
    },

    onMouseoverCircleMarkerBorder(e) {
      this.DomEvent.stopPropagation(e)
      this.updateCursorStyle(e)
    },

    onMousedownCircleMarkerBorder(e) {
      this.DomEvent.stopPropagation(e)

      const map = e.target._map
      map.dragging.disable()

      const onResizeMove = (moveEvent) => {
        const center = this.$centerMarker.getLatLng()

        const newRadius = Math.max(
          Math.round(map.distance(center, moveEvent.latlng)),
          this.minRadius,
        )

        this.$circleMarker.setRadius(newRadius)
        this.$circleMarkerBorder.setRadius(newRadius - this.strokeWeight / 2)
      }

      const onResizeEnd = () => {
        map.dragging.enable()

        map.off('mousemove', onResizeMove)
        map.off('mouseup', onResizeEnd)

        map.getContainer().style.cursor = ''

        this.$emit('update:radius', this.$circleMarker.getRadius())
      }

      map.on('mousemove', onResizeMove)
      map.on('mouseup', onResizeEnd)
    },

    onDragCenterMarker(e) {
      this.$circleMarker.setLatLng(e.latlng)
      this.$circleMarkerBorder.setLatLng(e.latlng)
    },

    onDragEndCentermarker() {
      this.$emit('update:location', Object.values(this.$centerMarker.getLatLng()))
    },

    onBorderOut(e) {
      e.target._map.getContainer().style.cursor = ''
    },

    enableEvents() {
      this.$centerMarker.on('click', this.onClickCenterMarker)
      this.$centerMarker.on('drag', this.onDragCenterMarker)
      this.$centerMarker.on('dragend', this.onDragEndCentermarker)

      this.$circleMarker.on('click', this.onClickCircleMarker)

      this.$circleMarkerBorder.on('click', this.onClickCircleMarkerBorder)
      this.$circleMarkerBorder.on('mouseover', this.onMouseoverCircleMarkerBorder)
      this.$circleMarkerBorder.on('mousemove', this.onMouseoverCircleMarkerBorder)
      this.$circleMarkerBorder.on('mouseout', this.onBorderOut)
      this.$circleMarkerBorder.on('mousedown', this.onMousedownCircleMarkerBorder)
    },

    disableEvents() {
      this.$centerMarker.off('click', this.onClickCenterMarker)
      this.$centerMarker.off('drag', this.onDragCenterMarker)
      this.$centerMarker.off('dragend', this.onDragEndCentermarker)

      this.$circleMarker.off('click', this.onClickCircleMarker)

      this.$circleMarkerBorder.off('click', this.onClickCircleMarkerBorder)
      this.$circleMarkerBorder.off('mouseover', this.onMouseoverCircleMarkerBorder)
      this.$circleMarkerBorder.off('mousemove', this.onMouseoverCircleMarkerBorder)
      this.$circleMarkerBorder.off('mouseout', this.onBorderOut)
      this.$circleMarkerBorder.off('mousedown', this.onMousedownCircleMarkerBorder)
    },
  },

  beforeDestroy() {
    this.disableEvents()

    this.onMap.then((map) => {
      if (this.$layerGroup) {
        map.removeLayer(this.$layerGroup)
      }
    })
  },
}
</script>

<style lang="scss">
@import 'variables';

.o-map-editable-circle-marker {
  &__icon {
    font-size: 26px;
  }

  &__zone {
    filter: drop-shadow(0px 0px 2px $color-secondary-lightest);

    &:hover {
      cursor: unset;
    }
  }

  &__tooltip {
    white-space: pre !important;
  }
}
</style>
